import * as React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Clock,
  Flag,
  Gavel,
  MessageSquarePlus,
  FileCheck2,
  ListChecks,
  LayoutDashboard,
  LogOut,
  Radio,
  Scale,
  Search as SearchIcon,
  Settings,
  TrendingUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { AppBrandMark } from "./AppBrandMark";
import { FloatingChatWidget } from "../FloatingChatWidget";
import { OVERVIEW_ONLY } from "../../lib/demoMode";
import { useRegulationHome } from "../../lib/useRegulationHome";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  mobileTab?: boolean;
  /** Shown to admins only. */
  adminOnly?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

/** The "Genie feedback" page (admins) is hidden from the sidebar for now; it still opens at /feedback. Flip to show it. */
const SHOW_GENIE_FEEDBACK_LINK = false;

/** The "Reports" and "Claims & Matters" groups are hidden from the sidebar for now (and the phone tab bar); their pages still open by URL.
 * Flip to show them again. */
const SHOW_REPORTS_AND_MATTERS = false;

/** The sidebar. */
function buildNavSections(): NavSection[] {
  const executive: NavSection[] = [
    {
      label: "Executive",
      items: [
        { to: "/overview", label: "Overview", icon: LayoutDashboard, mobileTab: true },
        { to: "/regulation", label: "HP Genie", icon: Scale, mobileTab: true },
        ...(SHOW_GENIE_FEEDBACK_LINK ? [{ to: "/feedback", label: "Genie feedback", icon: MessageSquarePlus, adminOnly: true }] : []),
        { to: "/questions", label: "Genie questions", icon: ListChecks, adminOnly: true },
        { to: "/facts", label: "Document facts", icon: FileCheck2, adminOnly: true },
      ],
    },
  ];
  if (!SHOW_REPORTS_AND_MATTERS) return executive;
  return [
    ...executive,
    {
      label: "Reports",
      items: [
        { to: "/reports/potential", label: "Potential Claims", icon: Flag, mobileTab: true },
        { to: "/reports/in-progress", label: "In Progress", icon: Clock },
        { to: "/reports/recovered", label: "Value Recovered", icon: TrendingUp },
        { to: "/exposure", label: "Rejected Claims", icon: AlertTriangle },
      ],
    },
    {
      label: "Claims & Matters",
      items: [
        { to: "/matters", label: "Matter Tracker", icon: Gavel, mobileTab: true },
        { to: "/search", label: "Search", icon: SearchIcon, mobileTab: true },
        { to: "/sources", label: "Sources", icon: Radio },
      ],
    },
  ];
}

/**
 * Overview-only mode shows every sidebar item (so the app reads as complete), but only "Overview"
 * itself is wired up - every other item renders disabled with a "Coming soon" pill instead of being
 * hidden or silently broken.
 */

function isNavItemEnabled(item: NavItem): boolean {
  return !OVERVIEW_ONLY || item.to === "/" || item.to === "/overview" || item.to === "/regulation" || item.to === "/feedback" || item.to === "/questions" || item.to === "/facts";
}

const PAGE_TITLES: Record<string, string> = {
  "/": "Overview",
  "/overview": "Overview",
  "/regulation": "HP Genie",
  "/feedback": "Genie feedback",
  "/questions": "Genie questions",
  "/facts": "Document facts",
  "/reports/potential": "Potential Claims",
  "/reports/in-progress": "In Progress Claims",
  "/reports/recovered": "Value Recovered",
  "/exposure": "Rejected Claims",
  "/matters": "Matter Tracker",
  "/search": "Search",
  "/sources": "Sources",
  "/settings": "Settings",
  "/pipeline": "Pipeline",
  "/digest": "Digest",
};

function initials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function isNavItemActive(pathname: string, search: string, item: NavItem): boolean {
  if (item.to.startsWith("/reports/")) {
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  }
  return pathname + search === item.to;
}

function resolvePageTitle(pathname: string): string {
  if (pathname.startsWith("/portfolio/")) return "Contract Detail";
  if (pathname.startsWith("/reports/potential/")) return "Claim Detail";
  if (pathname.startsWith("/reports/in-progress/")) return "Claim Detail";
  if (pathname.startsWith("/reports/recovered/")) return "Claim Detail";
  return PAGE_TITLES[pathname] ?? "Claims Radar";
}

function SidebarNavLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const active = isNavItemActive(location.pathname, location.search, item);
  const enabled = isNavItemEnabled(item);

  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={enabled ? undefined : `${item.label} - coming soon`}
      className={[
        "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
        !enabled
          ? "text-zinc-400 hover:bg-zinc-50 hover:text-zinc-500"
          : active
            ? "bg-sky-50 text-sky-800 shadow-xs ring-1 ring-sky-100"
            : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900",
      ].join(" ")}
      aria-current={active ? "page" : undefined}
    >
      <span
        className={[
          "flex h-8 w-8 flex-none items-center justify-center rounded-lg transition-colors",
          !enabled
            ? "bg-zinc-100 text-zinc-400"
            : active
              ? "bg-sky-100 text-sky-700"
              : "bg-zinc-100 text-zinc-500 group-hover:bg-zinc-200/70 group-hover:text-zinc-700",
        ].join(" ")}
      >
        <item.icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

/** The floating HP Genie button at the bottom right is hidden on every page for now (HP Genie is still in the sidebar);
 * flip to bring it back (it then also follows the setting "Make HP Genie the home page"). */
const SHOW_FLOATING_CHAT = false;

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = resolvePageTitle(location.pathname);
  const { regulationHome } = useRegulationHome();
  const isAdmin = user?.role === "admin";
  const genieOnly = user?.role === "user"; // HP Genie only: no Overview, no Settings
  const VISIBLE_NAV_SECTIONS = React.useMemo(
    () =>
      buildNavSections()
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => (!item.adminOnly || isAdmin) && (!genieOnly || item.to === "/regulation")),
        }))
        .filter((section) => section.items.length > 0),
    [isAdmin, genieOnly],
  );
  const MOBILE_TABS = VISIBLE_NAV_SECTIONS.flatMap((section) => section.items).filter(
    (item) => item.mobileTab && isNavItemEnabled(item),
  );

  return (
    <div className="flex min-h-dvh bg-zinc-50/80">
      {/* Persistent sidebar — always visible on md+ (Saronil-style) */}
      <aside
        className="sticky top-0 hidden h-dvh w-64 flex-none flex-col self-start border-r border-zinc-200/80 bg-white md:flex lg:w-72 2xl:w-80"
        aria-label="Main navigation"
      >
        <div className="border-b border-zinc-100 px-4 py-4">
          <AppBrandMark variant="sidebar" />
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {VISIBLE_NAV_SECTIONS.map((section) => (
            <div key={section.label} className="mb-6 last:mb-0">
              <div className="mb-2 px-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-zinc-400">
                {section.label}
              </div>
              <div className="flex flex-col gap-0.5">
                {section.items.map((item) => (
                  <SidebarNavLink key={item.to} item={item} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {user ? (
          <div className="border-t border-zinc-100 p-3">
            <div className="flex items-center gap-3 rounded-xl bg-zinc-50 px-3 py-2.5 ring-1 ring-zinc-100">
              <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[var(--color-accent)] text-xs font-semibold text-white">
                {initials(user.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-800">{user.name}</div>
                <div className="truncate text-xs capitalize text-zinc-500">{user.role}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  logout();
                  navigate("/login", { replace: true });
                }}
                title="Sign out"
                aria-label="Sign out"
                className="flex h-8 w-8 flex-none items-center justify-center rounded-lg text-zinc-400 transition-colors hover:bg-white hover:text-zinc-700"
              >
                <LogOut className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        ) : null}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar — no hamburger on desktop */}
        <header className="sticky top-0 z-30 flex flex-none items-center justify-between gap-4 border-b border-zinc-200/80 bg-white px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3 md:hidden">
            <AppBrandMark variant="compact" />
          </div>
          <div className="min-w-0 flex-1 md:flex-none">
            <h1 className="truncate font-serif text-lg font-semibold text-zinc-900 sm:text-xl">{pageTitle}</h1>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {OVERVIEW_ONLY ? null : (
              <NavLink
                to="/search"
                className="hidden items-center gap-2 rounded-lg border border-zinc-200 border-b-zinc-300/80 bg-white px-3 py-2 text-sm text-zinc-500 shadow-xs transition-colors hover:bg-zinc-50 sm:flex sm:min-w-[12rem] lg:min-w-[16rem]"
              >
                <SearchIcon className="h-4 w-4 flex-none" aria-hidden />
                <span>Search claims…</span>
              </NavLink>
            )}
            {genieOnly ? null : (
              <NavLink
                to="/settings"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800"
                aria-label="Settings"
              >
                <Settings className="h-4 w-4" aria-hidden />
              </NavLink>
            )}
            {user ? (
              <div
                className="hidden h-9 w-9 items-center justify-center rounded-full bg-[var(--color-accent)] text-xs font-semibold text-white sm:flex"
                title={user.name}
              >
                {initials(user.name)}
              </div>
            ) : null}
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4 pb-20 sm:px-6 sm:py-6 md:pb-6 lg:px-8 lg:py-8">
          <div className="content-frame">
            <Outlet />
          </div>
        </main>

        {/* Mobile bottom nav — replaces hamburger drawer */}
        {MOBILE_TABS.length > 1 ? (
        <nav
          className="fixed inset-x-0 bottom-0 z-40 flex border-t border-zinc-200 bg-white px-2 py-1.5 md:hidden"
          aria-label="Mobile navigation"
        >
          {MOBILE_TABS.map((item) => {
            const active = isNavItemActive(location.pathname, location.search, item);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={[
                  "flex flex-1 flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 text-[0.625rem] font-medium",
                  active ? "text-sky-700" : "text-zinc-500",
                ].join(" ")}
              >
                <span
                  className={[
                    "flex h-8 w-8 items-center justify-center rounded-lg",
                    active ? "bg-sky-50 text-sky-700" : "text-zinc-400",
                  ].join(" ")}
                >
                  <item.icon className="h-4 w-4" aria-hidden />
                </span>
                <span className="truncate">{item.label === "HP Genie" ? item.label : item.label.split(" ")[0]}</span>
              </NavLink>
            );
          })}
        </nav>
        ) : null}
      </div>

      {SHOW_FLOATING_CHAT && !regulationHome ? <FloatingChatWidget /> : null}
    </div>
  );
}
